import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { 
  getSLAs, 
  createSLA, 
  updateSLA, 
  deleteSLA,
  getSLAById,
  uploadSLAAttachment,
  getSLAAttachments,
  downloadSLAAttachment
} from '../../../models/slaModel';
import { getSuppliers } from '../../../models/suppliersModel';
import SearchableSelect from '../../../components/common/SearchableSelect';
import CustomDatePicker from '../../../components/common/CustomDatePicker';
import MultiFileInput from '../../../components/common/MultiFileInput';
import { toast } from 'react-toastify';

function SLAManagement() {
  const [slas, setSlas] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSLA, setEditingSLA] = useState(null);
  const [formData, setFormData] = useState({
    supplier_id: '',
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    notification_days: 30,
    status: 'active'
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  
  // Loading states for different operations
  const [loadingStates, setLoadingStates] = useState({
    submitting: false,
    deleting: false,
    downloading: false,
    loadingAttachments: false
  });
  
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Process suppliers to remove duplicates and create searchable format
  const processedSuppliers = React.useMemo(() => {
    if (!suppliers || suppliers.length === 0) return [];
    
    // Remove duplicates based on BP_Code or id
    const uniqueSuppliers = suppliers.reduce((acc, supplier) => {
      const key = supplier.BP_Code || supplier.id;
      const name = supplier.BP_Name || supplier.name;
      
      // Skip if no key or name
      if (!key || !name) return acc;
      
      // Skip if already exists
      if (acc.find(s => (s.value === key))) return acc;
      
      acc.push({
        value: key,
        label: name
      });
      
      return acc;
    }, []);
    
    // Sort alphabetically by label
    return uniqueSuppliers.sort((a, b) => a.label.localeCompare(b.label));
  }, [suppliers]);

  // Fetch SLAs and suppliers
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [slasResponse, suppliersResponse] = await Promise.all([
        getSLAs({
          page: currentPage,
          limit: pageSize,
          search: searchTerm,
          status: statusFilter !== 'all' ? statusFilter : undefined
        }),
        getSuppliers(1, 1000) // Get suppliers for dropdown
      ]);

      if (slasResponse.status === 1) {
        setSlas(slasResponse.data.data || []);
        setTotalCount(slasResponse.data.total || 0);
      } else {
        console.error('Failed SLA API response:', slasResponse);
        toast.error(slasResponse.message || 'Failed to load SLAs');
        setSlas([]);
        setTotalCount(0);
      }

      if (suppliersResponse.status === 'success') {
        console.log('Suppliers loaded successfully:', suppliersResponse.data); // Debug log
        setSuppliers(suppliersResponse.data || []);
      } else {
        console.error('Failed suppliers API response:', suppliersResponse);
        setSuppliers([]);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('An error occurred while loading data');
      setSlas([]);
      setSuppliers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
    } catch (error) {
      console.error('Date formatting error:', error);
    }
    
    return 'N/A';
  };

  // Convert date from dd/MM/yyyy to yyyy-MM-dd for backend
  const convertDateForBackend = (dateString) => {
    if (!dateString) return '';
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) return dateString; // Already in correct format
    
    // Convert from dd/MM/yyyy to yyyy-MM-dd
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return dateString;
  };

  // Convert date from yyyy-MM-dd to dd/MM/yyyy for display
  const convertDateForDisplay = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '';
    if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) return dateString; // Already in display format
    
    // Convert from yyyy-MM-dd to dd/MM/yyyy
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1].padStart(2, '0');
      const day = parts[2].padStart(2, '0');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  };

  const getDaysToExpiry = (endDate) => {
    if (!endDate || endDate === '0000-00-00') return null;
    
    try {
      const now = new Date();
      const expiry = new Date(endDate);
      const diffTime = expiry - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (error) {
      return null;
    }
  };

  const getStatusDisplay = (sla) => {
    const daysToExpiry = getDaysToExpiry(sla.end_date);
    const notificationDays = sla.notification_days || 30;
    
    if (sla.status === 'inactive') {
      return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-gray-100 text-gray-800">Inactive</span>;
    }
    
    if (daysToExpiry !== null) {
      if (daysToExpiry < 0) {
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-red-100 text-red-800">Expired</span>;
      } else if (daysToExpiry <= notificationDays) {
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-yellow-100 text-yellow-800">Expiring Soon</span>;
      }
    }
    
    return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-green-100 text-green-800">Active</span>;
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      notification_days: 30,
      status: 'active'
    });
    setSelectedFiles([]);
    setAttachments([]);
    setEditingSLA(null);
  };

  const handleCreateClick = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleEditClick = async (sla) => {
    try {
      const response = await getSLAById(sla.id);
      if (response.status === 1 && response.data) {
        const slaData = response.data;
        setFormData({
          supplier_id: slaData.supplier_id || '',
          title: slaData.title || '',
          description: slaData.description || '',
          start_date: convertDateForDisplay(slaData.start_date),
          end_date: convertDateForDisplay(slaData.end_date),
          notification_days: slaData.notification_days || 30,
          status: slaData.status || 'active'
        });
        setEditingSLA(slaData);
        
        // Load attachments for this SLA
        try {
          const attachmentsResponse = await getSLAAttachments(sla.id);
          if (attachmentsResponse.status === 1) {
            setAttachments(attachmentsResponse.attachments || []);
          }
        } catch (attachmentError) { 
          setAttachments([]);
        }
        
        setShowCreateModal(true);
      } else {
        toast.error('Failed to load SLA details');
      }
    } catch (error) {
      console.error('Error loading SLA for editing:', error);
      toast.error('Failed to load SLA details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loadingStates.submitting) return; // Prevent multiple submissions
    
    setLoadingStates(prev => ({ ...prev, submitting: true }));
    
    try {
      console.log('Form data before submission:', formData); // Debug log
      
      // First, create/update the SLA without file
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        let value = formData[key];
        // Convert dates to backend format
        if (key === 'start_date' || key === 'end_date') {
          value = convertDateForBackend(value);
        }
        console.log(`Adding to FormData: ${key} = ${value}`); // Debug log
        submitData.append(key, value);
      });

      let result;
      if (editingSLA) {
        result = await updateSLA(editingSLA.id, submitData);
      } else {
        result = await createSLA(submitData);
      }

      if (result.status === 1) {
        const slaId = editingSLA ? editingSLA.id : result.sla_id;
        
        // Handle file upload separately using the standardized attachment system
        if (selectedFiles.length > 0 && slaId) {
          try {
            for (const file of selectedFiles) {
              const fileFormData = new FormData();
              fileFormData.append('file', file);
              
              const uploadResult = await uploadSLAAttachment(slaId, fileFormData);
              if (uploadResult.status !== 1) {
                console.error('File upload failed:', uploadResult);
                toast.warning(`SLA saved but file "${file.name}" upload failed: ` + (uploadResult.message || 'Unknown error'));
              }
            }
          } catch (uploadError) {
            console.error('File upload error:', uploadError);
            toast.warning('SLA saved but file upload failed');
          }
        }
        
        toast.success(editingSLA ? 'SLA updated successfully' : 'SLA created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchData();
      } else {
        console.error('SLA creation failed:', result); // Debug log
        toast.error(result.message || 'Failed to save SLA');
      }
    } catch (error) {
      console.error('Error saving SLA:', error);
      toast.error('An error occurred while saving the SLA');
    } finally {
      setLoadingStates(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleDeleteClick = async (sla) => {
    if (!window.confirm(`Are you sure you want to delete the SLA "${sla.title}"?`)) {
      return;
    }

    try {
      const result = await deleteSLA(sla.id);
      if (result.status === 1) {
        toast.success('SLA deleted successfully');
        fetchData();
      } else {
        toast.error(result.message || 'Failed to delete SLA');
      }
    } catch (error) {
      console.error('Error deleting SLA:', error);
      toast.error('An error occurred while deleting the SLA');
    }
  };

  const handleDownloadDocument = async (sla) => {
    try {
      // Use the standardized attachment download
      const result = await downloadSLAAttachment(sla.id);
      if (result.status === 1) {
        toast.success('Document download started');
      } else {
        toast.error(result.message || 'Download failed');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  if (loading && slas.length === 0) {
    return (
      <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 py-1">
      {/* Professional Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between">
          {/* Left: Icon, Title, Description, Results */}
          <div className="flex items-start">
            <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <svg className="h-5 w-5" fill="none" stroke="#0096DE" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">SLA Management</h1>
              <p className="text-xs text-gray-500 mb-1">
                Service Level Agreements & Document Management
              </p>
              <div className="flex items-center mt-1">
                <span className="text-xs font-medium" style={{ color: '#0096DE' }}>
                  {totalCount ? `Showing ${Math.min(pageSize, totalCount)} of ${totalCount} results` : '0 results'}
                  {(searchTerm || statusFilter !== 'all') && (
                    <span className="ml-1 text-blue-600">
                      (filtered)
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
          
          {/* Right: All Controls and Search */}
          <div className="flex flex-col gap-2 items-end min-w-0">
            {/* Row 1: All Controls on Right */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Clear Filters Button (only show if filters are active) */}
              {(searchTerm || statusFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                  className="inline-flex items-center px-2 py-1.5 border border-red-300 rounded-md shadow-sm text-xs text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-500 flex-shrink-0"
                >
                  Clear Filters
                </button>
              )}
              
              {/* Refresh Button */}
              <button 
                onClick={() => fetchData()} 
                className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                title="Refresh"
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0096DE' : 'currentColor'} style={{ color: loading ? '#0096DE' : 'currentColor' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              
              {/* Status Filter */}
              <div className="flex-shrink-0">
                <select
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  className={`text-sm px-2 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    statusFilter !== 'all' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white'
                  }`}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              {/* Page Size Filter */}
              <div className="flex-shrink-0">
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 30, 50].map(size => (
                    <option key={size} value={size}>{size} per page</option>
                  ))}
                </select>
              </div>

              {/* Create Button */}
              {(can('manage_slas') || can('procurement_admin') || user) && (
                <button
                  onClick={handleCreateClick}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1 flex-shrink-0"
                  style={{ backgroundColor: '#0096DE', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: '#0096DE' }}
                >
                  <svg className="-ml-0.5 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  New SLA
                </button>
              )}
            </div>
            
            {/* Search Box in Row 2 */}
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search SLAs..."
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Simple Table */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>ID</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Title</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Supplier</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Start Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>End Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Days to Expiry</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Document</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Status</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan="9" className="px-3 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading...</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && slas.length === 0 && (
                <tr>
                  <td colSpan="9" className="px-3 py-6 text-center">
                    <p className="text-gray-600">No SLAs found</p>
                  </td>
                </tr>
              )}
              {!loading && slas.map(sla => {
                const daysToExpiry = getDaysToExpiry(sla.end_date);
                const supplierName = sla.supplier_name || 'Unknown Supplier';
                
                return (
                  <tr key={sla.id} className="hover:bg-blue-50">
                    <td className="px-3 py-2 text-sm">{sla.id}</td>
                    <td className="px-3 py-2 text-sm font-medium">{sla.title}</td>
                    <td className="px-3 py-2 text-sm">{supplierName}</td>
                    <td className="px-3 py-2 text-sm">{formatDate(sla.start_date)}</td>
                    <td className="px-3 py-2 text-sm">{formatDate(sla.end_date)}</td>
                    <td className="px-3 py-2 text-sm">
                      {daysToExpiry !== null ? (
                        <span className={daysToExpiry < 0 ? 'text-red-600 font-semibold' : daysToExpiry <= (sla.notification_days || 30) ? 'text-yellow-600 font-semibold' : 'text-green-600'}>
                          {daysToExpiry < 0 ? `Expired ${Math.abs(daysToExpiry)} days ago` : `${daysToExpiry} days`}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {sla.has_attachments ? (
                        <button
                          onClick={() => handleDownloadDocument(sla)}
                          className="text-blue-600 hover:text-blue-800 text-xs"
                        >
                          Download
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">No document</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm">{getStatusDisplay(sla)}</td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center space-x-2">
                        {(can('manage_slas') || can('procurement_admin') || user) && (
                          <>
                            <button
                              onClick={() => handleEditClick(sla)}
                              className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteClick(sla)}
                              className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Enhanced Pagination */}
        {totalCount > 0 && (
          <div className="bg-gray-50 px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-4">
              <div className="text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </div>
              
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="text-gray-500">per page</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pageNumbers = [];
                  
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1.5 border rounded text-xs font-medium ${
                          i === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  return pageNumbers;
                })()}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowCreateModal(false)}>
          <div className="relative top-10 mx-auto p-6 border max-w-2xl w-full mx-4 shadow-lg rounded-lg bg-white" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 border-b pb-3">
                {editingSLA ? 'Edit SLA' : 'Create New SLA'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Supplier - Full Width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supplier *
                  </label>
                  <SearchableSelect
                    items={processedSuppliers}
                    onSelectItem={(selectedItem) => {
                      setFormData(prev => ({
                        ...prev,
                        supplier_id: selectedItem ? selectedItem.value : ''
                      }));
                    }}
                    initialSelectedItem={formData.supplier_id 
                      ? processedSuppliers.find(item => item.value === formData.supplier_id)
                      : null}
                    placeholder="Select a supplier"
                    required
                  />
                </div>
                
                {/* Title - Full Width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="SLA Title"
                  />
                </div>
                
                {/* Description - Full Width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="SLA Description"
                  />
                </div>
                
                {/* Row 3: Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <CustomDatePicker
                      value={formData.start_date}
                      onChange={(date) => setFormData({...formData, start_date: date})}
                      placeholder="Select start date"
                      required
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date *
                    </label>
                    <CustomDatePicker
                      value={formData.end_date}
                      onChange={(date) => setFormData({...formData, end_date: date})}
                      placeholder="Select end date"
                      required
                      className="w-full"
                    />
                  </div>
                </div>
                
                {/* Row 4: Notification Days and Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notification Days
                    </label>
                    <input
                      type="number"
                      value={formData.notification_days}
                      onChange={(e) => setFormData({...formData, notification_days: e.target.value})}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="30"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                
                {/* Row 5: File Upload (full width) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SLA Documents
                  </label>
                  <MultiFileInput
                    files={selectedFiles}
                    onChange={setSelectedFiles}
                    accept=".pdf,.doc,.docx,.txt"
                    multiple={true}
                    className="w-full"
                  />
                  
                  {/* Show existing attachments if editing */}
                  {editingSLA && attachments.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Existing Documents ({attachments.length})
                      </h4>
                      <div className="space-y-2">
                        {attachments.map((attachment) => (
                          <div key={attachment.attachment_id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {attachment.original_filename}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {attachment.uploaded_at ? new Date(attachment.uploaded_at).toLocaleDateString() : 'Unknown date'}
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDownloadDocument({ id: editingSLA.id })}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Download
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    {editingSLA ? 'Update SLA' : 'Create SLA'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SLAManagement;